import "./i18n";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { AuthProvider } from "./context/authContext";

import PortalSeo from "./components/SEO/PortalSeo";
import RootLayout from "./components/RootLayout/RootLayout";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import RoleBasedRoute from "./components/RoleBasedRoute/RoleBasedRoute";

import PortalPage from "./pages/portal/PortalPage";
import AdminPage from "./pages/admin/AdminPage";
import UserInfoPage from "./pages/userinfo/UserInfoPage";
import CreateProjectPage from "./pages/create-project/CreateProjectPage";
import TraficPage from "./pages/trafic/TraficPage";
import PageSpeedPage from "./pages/pagespeed/PageSpeedPage";
import ProductsPage from "./pages/products/ProductsPage";
import BillingPage from "./pages/billing/BillingPage";
import SettingsPage from "./pages/settings/SettingsPage";
import ProfilePage from "./pages/profile/ProfilePage";
import SupportPage from "./pages/support/SupportPage";
import CheckoutPage from "./pages/checkout/CheckoutPage";
import AuthPage from "./pages/auth/AuthPage";
import PaymentSuccessPage from "./pages/payment/PaymentSuccessPage";
import PaymentFailurePage from "./pages/payment/PaymentFailurePage";
import SubscriptionPage from "./pages/subscription/SubscriptionPage";
import { useEffect, useState } from "react";
import LoadingOverlay from "./components/UI/LoadingOverlay/LoadingOverlay";
import RegisterPage from "./pages/register/RegisterPage";
import AccessDeniedPage from "./pages/access-denied/AccessDeniedPage";
import ForgotPasswordPage from "./pages/forgot-password/ForgotPasswordPage";
import ResetPasswordPage from "./pages/reset-password/ResetPasswordPage";
import OwnerRegisterPage from "./pages/owner-register/OwnerRegisterPage";
import NotFoundPage from "./pages/not-found/NotFoundPage";

function AppShell() {
  return (
    <>
      <PortalSeo />
      <Outlet />
    </>
  );
}

const route = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <RootLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/",
        element: <PortalPage />,
      },
      {
        path: "/traffic",
        element: <TraficPage />,
      },
      {
        path: "/seo",
        element: <PageSpeedPage />,
      },
      {
        path: "/admin",
        element: (
          <RoleBasedRoute allowedRoles={["admin", "owner"]}>
            <AdminPage />
          </RoleBasedRoute>
        ),
      },
      {
        path: "/user-info",
        element: (
          <RoleBasedRoute allowedRoles={["admin", "owner"]}>
            <UserInfoPage />
          </RoleBasedRoute>
        ),
      },
      {
        path: "/create-project",
        element: (
          <RoleBasedRoute allowedRoles={["admin", "owner"]}>
            <CreateProjectPage />
          </RoleBasedRoute>
        ),
      },
      {
        path: "/products",
        element: <ProductsPage />,
      },
      {
        path: "/billing",
        element: <BillingPage />,
      },
      {
        path: "/checkout",
        element: <CheckoutPage />,
      },
      {
        path: "/settings",
        element: <SettingsPage />,
      },
      {
        path: "/profile",
        element: <ProfilePage />,
      },
      {
        path: "/support",
        element: <SupportPage />,
      },
      {
        path: "/subscription",
        element: <SubscriptionPage />,
      },
      {
        path: "/payment-success",
        element: <PaymentSuccessPage />,
      },
      {
        path: "/payment-failure",
        element: <PaymentFailurePage />,
      },
    ],
  },
  {
    path: "/auth",
    element: <AuthPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/access-denied",
    element: <AccessDeniedPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/owner-register",
    element: <OwnerRegisterPage />,
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
    ],
  },
]);

function App() {
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleLoadingComplete = () => {
    setIsInitialLoading(false);
  };

  return (
    <>
      <LoadingOverlay
        isVisible={isInitialLoading}
        onComplete={handleLoadingComplete}
      />
      {!isInitialLoading && (
        <AuthProvider>
          <RouterProvider router={route} />
        </AuthProvider>
      )}
    </>
  );
}

export default App;
